import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Spinner, Select, Button, PageHeader } from '../components';
import { SettingsSkeleton } from '../skeletons';
import { profileService } from '../services';
import { User as UserIcon, Mail, Phone, Lock, Shield, Camera, Check } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { pinProblem } from '../utils/pin';

/**
 * Settings — self-service profile page used by admin, managers, and
 * staff alike. Editable fields are driven by the backend's
 * `editable_fields` array which is computed from the user's role_code:
 *
 *   - admin / carehome_manager / domiciliary_manager → full edit + role
 *   - staffs / employees                              → email + phone only
 *
 * Password and avatar uploads sit on dedicated endpoints (POST /me/password,
 * POST /me/avatar) so the UI surfaces them as separate cards.
 */

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
};

const API_BASE = (() => {
  // axiosInstance points at /api; the avatar URL is /uploads/... so we
  // strip the trailing /api off whatever VITE_API_URL is set to.
  const raw = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
  return raw.replace(/\/api\/?$/, '');
})();

const Settings = () => {
  const { user: ctxUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [me, setMe] = useState(null);
  const [editableFields, setEditableFields] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);

  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    role_id: '',
  });
  const [passwordForm, setPasswordForm] = useState({ current_pin: '', new_pin: '', confirm_pin: '' });

  const fileInputRef = useRef(null);

  const can = (f) => editableFields.includes(f);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await profileService.getMe();
      if (res.success) {
        const u = res.data.user;
        setMe(u);
        setEditableFields(res.data.editable_fields || []);
        setProfileForm({
          name: u.name || '',
          email: u.email || '',
          phone: u.phone || '',
          role_id: u.role_id || '',
        });
        // Only load role options if the backend says the user can change role.
        if ((res.data.editable_fields || []).includes('role_id')) {
          const roles = await profileService.getRoleOptions();
          if (roles.success) setRoleOptions(roles.data || []);
        }
      }
    } catch (err) {
      toast.error('Could not load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSaveProfile = async (e) => {
    e?.preventDefault?.();
    try {
      setSavingProfile(true);
      // Send only fields the user is allowed to change. Sending extras is
      // harmless (the backend filters), but keeps payloads clean.
      const payload = {};
      ['name', 'email', 'phone', 'role_id'].forEach((f) => {
        if (can(f)) payload[f] = profileForm[f];
      });
      const res = await profileService.updateMe(payload);
      if (res.success) {
        toast.success('Profile updated');
        setMe(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault?.();
    if (!passwordForm.current_pin || !passwordForm.new_pin) {
      toast.error('Fill both fields');
      return;
    }
    const pinIssue = pinProblem(passwordForm.new_pin);
    if (pinIssue) {
      toast.error(pinIssue);
      return;
    }
    if (passwordForm.new_pin !== passwordForm.confirm_pin) {
      toast.error('New password and confirmation do not match');
      return;
    }
    try {
      setSavingPassword(true);
      const res = await profileService.changePassword({
        current_pin: passwordForm.current_pin,
        new_pin: passwordForm.new_pin,
      });
      if (res.success) {
        toast.success('Password updated');
        setPasswordForm({ current_pin: '', new_pin: '', confirm_pin: '' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarPick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }
    try {
      setUploadingAvatar(true);
      const res = await profileService.uploadAvatar(file);
      if (res.success) {
        toast.success('Profile picture updated');
        setMe((m) => ({ ...m, avatar_url: res.data.avatar_url }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  if (loading) return <SettingsSkeleton />;
  if (!me) return null;

  const avatarSrc = me.avatar_url ? `${API_BASE}${me.avatar_url}` : null;
  const roleName = me.Role?.role_name || ctxUser?.role?.role_name || 'Member';
  const canAvatar = can('avatar_url');
  const canRole = can('role_id');

  return (
    <div className="w-full max-w-[1040px] mx-auto pb-10">
      <PageHeader
        title="Settings"
        subtitle="Manage your account, contact details, and password. Some fields are limited based on your role."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-5 mt-6 items-start">
        {/* LEFT — sticky profile card */}
        <div
          className="relative overflow-hidden bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-6 lg:sticky lg:top-4"
          style={{ borderTopColor: '#2563EB', borderTopWidth: '3px' }}
        >
          {/* soft accent glow */}
          <span className="pointer-events-none absolute -right-16 -top-16 w-[220px] h-[220px] rounded-full bg-[radial-gradient(circle,_rgba(37,99,235,0.08),_transparent_70%)]" />
          <div className="relative flex flex-col items-center text-center">
            <div className="relative">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-[28px] font-black shadow-sm overflow-hidden">
                {avatarSrc ? (
                  <img src={avatarSrc} alt={me.name} className="w-full h-full object-cover" />
                ) : (
                  initials(me.name)
                )}
              </div>
              {canAvatar && (
                <button
                  onClick={handleAvatarPick}
                  disabled={uploadingAvatar}
                  title="Change profile picture"
                  className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-full bg-[#2563EB] text-white border-2 border-white shadow-md flex items-center justify-center hover:bg-blue-700 transition disabled:opacity-60"
                >
                  {uploadingAvatar ? <Spinner /> : <Camera className="w-4 h-4" />}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={handleAvatarChange}
              />
            </div>
            <p className="text-[19px] font-extrabold text-[#0B1220] dark:text-gray-100 truncate max-w-full mt-4 leading-tight">{me.name}</p>
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 truncate max-w-full mt-0.5">{me.user_id ? `ID: ${me.user_id}` : 'No user ID'}</p>
            <span className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-lg bg-[#EEF3FF] dark:bg-blue-900/20 text-[#2563EB] text-[10px] font-black uppercase tracking-tight border border-[rgba(37,99,235,0.14)]">
              <Shield className="w-3 h-3" />
              {roleName}
            </span>
          </div>

          {/* Contact quick-view */}
          <div className="relative mt-6 space-y-3">
            <div className="rounded-2xl border border-[#EEF2F8] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c] px-4 py-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] dark:text-gray-500">
                <Mail className="w-3.5 h-3.5" /> Email
              </div>
              <p className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 mt-1 truncate">{me.email || '—'}</p>
            </div>
            <div className="rounded-2xl border border-[#EEF2F8] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c] px-4 py-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] dark:text-gray-500">
                <Phone className="w-3.5 h-3.5" /> Phone
              </div>
              <p className="text-[13px] font-semibold text-[#1E293B] dark:text-gray-200 mt-1 truncate">{me.phone || '—'}</p>
            </div>
          </div>
        </div>

        {/* RIGHT — forms */}
        <div className="space-y-5 min-w-0">

      {/* Editable profile fields */}
      <form onSubmit={handleSaveProfile} className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-6">
        <h2 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200 mb-1">Profile details</h2>
        <p className="text-[12px] text-[#64748B] dark:text-gray-400 mb-5">
          {editableFields.length === 0
            ? 'Your role does not allow editing profile fields.'
            : 'Fields you can edit are highlighted; others are read-only.'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5 flex items-center gap-1.5">
              <UserIcon className="w-3.5 h-3.5 text-[#94A3B8]" /> Name
            </label>
            <input
              type="text"
              value={profileForm.name}
              onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              disabled={!can('name')}
              className={`w-full px-3 py-2.5 rounded-xl border outline-none text-[13px] font-medium ${
                can('name')
                  ? 'border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10'
                  : 'border-[#F1F5F9] bg-[#F8FAFC] text-[#64748B] dark:border-gray-800 dark:bg-[#20242c] dark:text-gray-500 cursor-not-allowed'
              }`}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-[#94A3B8]" /> Email
            </label>
            <input
              type="email"
              value={profileForm.email}
              onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
              disabled={!can('email')}
              className={`w-full px-3 py-2.5 rounded-xl border outline-none text-[13px] font-medium ${
                can('email')
                  ? 'border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10'
                  : 'border-[#F1F5F9] bg-[#F8FAFC] text-[#64748B] dark:border-gray-800 dark:bg-[#20242c] dark:text-gray-500 cursor-not-allowed'
              }`}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5 flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-[#94A3B8]" /> Phone
            </label>
            <input
              type="tel"
              value={profileForm.phone}
              onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              disabled={!can('phone')}
              className={`w-full px-3 py-2.5 rounded-xl border outline-none text-[13px] font-medium ${
                can('phone')
                  ? 'border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10'
                  : 'border-[#F1F5F9] bg-[#F8FAFC] text-[#64748B] dark:border-gray-800 dark:bg-[#20242c] dark:text-gray-500 cursor-not-allowed'
              }`}
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5 flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-[#94A3B8]" /> Role
            </label>
            {canRole ? (
              <Select
                value={profileForm.role_id}
                onChange={(v) => setProfileForm({ ...profileForm, role_id: Number(v) })}
                options={roleOptions.map((r) => ({ value: String(r.id), label: r.role_name }))}
                className="w-full"
              />
            ) : (
              <input
                type="text"
                value={roleName}
                disabled
                className="w-full px-3 py-2.5 rounded-xl border border-[#F1F5F9] bg-[#F8FAFC] text-[#64748B] dark:border-gray-800 dark:bg-[#20242c] dark:text-gray-500 cursor-not-allowed text-[13px] font-medium"
              />
            )}
          </div>
        </div>

        <div className="flex justify-end pt-5 mt-5 border-t border-[#EEF2F8] dark:border-gray-800">
          <Button
            type="submit"
            variant="primary"
            loading={savingProfile}
            disabled={savingProfile || editableFields.length === 0}
          >
            {savingProfile ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>

      {/* Password card */}
      <form onSubmit={handleChangePassword} className="bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)] p-6">
        <h2 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-200 mb-1 flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#2563EB]" /> Password
        </h2>
        <p className="text-[12px] text-[#64748B] dark:text-gray-400 mb-5">
          Enter your current password to confirm the change, then your new password twice.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Current password</label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={passwordForm.current_pin}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">New password</label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={passwordForm.new_pin}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Confirm new password</label>
            <input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={passwordForm.confirm_pin}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="flex justify-end pt-5 mt-5 border-t border-[#EEF2F8] dark:border-gray-800">
          <Button
            type="submit"
            variant="primary"
            loading={savingPassword}
            disabled={savingPassword}
          >
            {savingPassword ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
